// Regras de acesso da organização (Etapa 18): funções puras do painel (sem React, sem rede).
//
// O owner/admin define quando (dias e horários) e de onde (faixas de IP) os members podem usar as
// credenciais, e em quais sites a extensão não oferece nada. A API é /access-policy (só owner/admin).
// As regras valem só para members: owner e admin nunca são bloqueados.
// Horário: um geral para todos os membros (`allowed_hours`) e, se precisar, um próprio por membro
// (`member_hours`), que substitui o geral só para aquela pessoa (lista vazia = sem limite para ela).

import { ApiRequestError } from '../api/client';

export interface FaixaDeHorario {
  /** 1 = segunda ... 7 = domingo */
  dias: number[];
  /** "HH:MM" */
  inicio: string;
  fim: string;
}

export interface PoliticaDeAcesso {
  allowed_hours: FaixaDeHorario[];
  /** id do membro → horário próprio (vazio = esse membro não tem limite de horário). */
  member_hours: Record<string, FaixaDeHorario[]>;
  timezone: string;
  allowed_cidrs: string[];
  blocked_domains: string[];
  updated_at?: string | null;
}

export const FUSO_PADRAO = 'America/Sao_Paulo';

export const POLITICA_VAZIA: PoliticaDeAcesso = {
  allowed_hours: [],
  member_hours: {},
  timezone: FUSO_PADRAO,
  allowed_cidrs: [],
  blocked_domains: [],
  updated_at: null,
};

/** Faixa nova (botão "Adicionar horário"): o horário comercial mais comum. */
export const FAIXA_PADRAO: FaixaDeHorario = { dias: [1, 2, 3, 4, 5], inicio: '08:00', fim: '19:00' };

/** Nome do dia (1 = segunda) e a abreviação dos checkboxes. */
export const DIAS_DA_SEMANA: { numero: number; nome: string; curto: string }[] = [
  { numero: 1, nome: 'segunda', curto: 'Seg' },
  { numero: 2, nome: 'terça', curto: 'Ter' },
  { numero: 3, nome: 'quarta', curto: 'Qua' },
  { numero: 4, nome: 'quinta', curto: 'Qui' },
  { numero: 5, nome: 'sexta', curto: 'Sex' },
  { numero: 6, nome: 'sábado', curto: 'Sáb' },
  { numero: 7, nome: 'domingo', curto: 'Dom' },
];

const nomeDoDia = (numero: number) => DIAS_DA_SEMANA.find((dia) => dia.numero === numero)?.nome ?? String(numero);

/** "a", "a e b", "a, b e c" */
function juntarComE(itens: string[]): string {
  if (itens.length <= 1) return itens[0] ?? '';
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** "de segunda a sexta", "todos os dias", "no sábado", "na segunda", "nos dias segunda, quarta e sexta". */
export function descreverDias(dias: number[]): string {
  const lista = [...new Set(dias)].filter((dia) => dia >= 1 && dia <= 7).sort((a, b) => a - b);
  if (lista.length === 7) return 'todos os dias';
  if (lista.length === 1) return `${lista[0] >= 6 ? 'no' : 'na'} ${nomeDoDia(lista[0])}`;
  const seguidos = lista.every((dia, indice) => indice === 0 || dia === lista[indice - 1] + 1);
  if (seguidos && lista.length >= 3) return `de ${nomeDoDia(lista[0])} a ${nomeDoDia(lista[lista.length - 1])}`;
  return `nos dias ${juntarComE(lista.map(nomeDoDia))}`;
}

/** "de segunda a sexta, das 08:00 às 19:00" (faixas sem dia não contam); sem faixas, "a qualquer dia e horário". */
export function descreverHorario(faixas: FaixaDeHorario[]): string {
  const validas = faixas.filter((faixa) => faixa.dias.length > 0);
  return validas.length === 0
    ? 'a qualquer dia e horário'
    : juntarComE(validas.map((faixa) => `${descreverDias(faixa.dias)}, das ${faixa.inicio} às ${faixa.fim}`));
}

/**
 * Prévia em português do que a política faz (item 6 da etapa), por exemplo:
 * "Membros poderão usar credenciais de segunda a sexta, das 08:00 às 19:00, apenas da rede 200.100.50.0/24."
 */
export function previaDaPolitica(
  politica: Pick<PoliticaDeAcesso, 'allowed_hours' | 'allowed_cidrs' | 'blocked_domains'> & Partial<Pick<PoliticaDeAcesso, 'member_hours'>>
): string {
  const horario = descreverHorario(politica.allowed_hours);
  const redes = politica.allowed_cidrs;
  const rede =
    redes.length === 0 ? 'de qualquer rede' : redes.length === 1 ? `apenas da rede ${redes[0]}` : `apenas das redes ${juntarComE(redes)}`;
  const comHorarioProprio = Object.keys(politica.member_hours ?? {}).length;
  let texto = `Membros poderão usar credenciais ${horario}, ${rede}.`;
  if (comHorarioProprio > 0) {
    texto += comHorarioProprio === 1
      ? ' Um membro tem horário próprio, no lugar desse.'
      : ` ${comHorarioProprio} membros têm horário próprio, no lugar desse.`;
  }
  if (politica.blocked_domains.length > 0) {
    texto += ` A extensão não oferecerá nem preencherá nada em ${juntarComE(politica.blocked_domains)} (nem nos subdomínios).`;
  }
  return texto;
}

/** Texto da caixa (um item por linha; vírgulas e espaços também separam) → lista sem vazios nem repetidos. */
export function textoParaLista(texto: string): string[] {
  const itens = texto
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return [...new Set(itens)];
}

/** Lista → texto da caixa (um item por linha). */
export function listaParaTexto(lista: string[]): string {
  return lista.join('\n');
}

/**
 * 409 do salvar com `code: "ip_outside_ranges"`: as faixas de IP deixam de fora o IP de quem está
 * salvando. Devolve o IP (ou "não identificado") para a tela pedir a confirmação; outro erro → null.
 */
export function ipForaDasFaixas(erro: unknown): string | null {
  if (!(erro instanceof ApiRequestError) || erro.status !== 409) return null;
  const detalhe = (erro.detail as { detail?: { code?: unknown; ip?: unknown } } | undefined)?.detail;
  if (!detalhe || detalhe.code !== 'ip_outside_ranges') return null;
  return typeof detalhe.ip === 'string' && detalhe.ip ? detalhe.ip : 'não identificado';
}

/** Texto da confirmação extra ao salvar uma regra de IP que exclui o IP atual de quem salva. */
export function textoDaConfirmacaoDeIp(ip: string): string {
  return (
    `O seu IP atual (${ip}) não está nas faixas de IP informadas. ` +
    'Membros que estiverem fora dessas faixas não poderão usar as credenciais. Salvar mesmo assim?'
  );
}

/** Mensagem para o que deu errado ao salvar/testar (422 traz a lista de campos inválidos do servidor). */
export function mensagemDeErroDaPolitica(erro: unknown): string {
  if (erro instanceof ApiRequestError) {
    if (erro.status === 403) return 'Apenas administradores e proprietários podem gerenciar as regras de acesso.';
    if (erro.status === 422) {
      const lista = (erro.detail as { detail?: unknown } | undefined)?.detail;
      const primeira = Array.isArray(lista) ? lista.find((item) => typeof item?.msg === 'string') : null;
      if (primeira) {
        const texto = String(primeira.msg).replace(/^Value error,\s*/i, '');
        return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}.`;
      }
      return 'Confira os campos: há algum valor inválido.';
    }
    if (erro.status === 409) return erro.message;
  }
  return 'Não foi possível falar com o Lictor agora. Tente novamente em instantes.';
}

/** Corpo do PUT a partir do formulário. */
export function corpoParaSalvar(politica: PoliticaDeAcesso, confirmarIp: boolean) {
  const limpar = (faixas: FaixaDeHorario[]) => faixas.map(({ dias, inicio, fim }) => ({ dias, inicio, fim }));
  return {
    allowed_hours: limpar(politica.allowed_hours),
    member_hours: Object.fromEntries(Object.entries(politica.member_hours ?? {}).map(([id, faixas]) => [id, limpar(faixas)])),
    timezone: politica.timezone || FUSO_PADRAO,
    allowed_cidrs: politica.allowed_cidrs,
    blocked_domains: politica.blocked_domains,
    confirm_ip_lockout: confirmarIp,
  };
}
